import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { Lock, AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import PasswordRequirements, { validatePassword } from '../components/PasswordRequirements';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const ChangePasswordFirstLogin = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const { toast } = useToast();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, text: '', color: '' });

  useEffect(() => {
    // If user is not logged in or not first login, redirect
    if (!token || !user) {
      navigate('/signin');
    } else if (!user.first_login) {
      navigate('/home');
    }
  }, [token, user, navigate]);

  useEffect(() => {
    checkPasswordStrength(newPassword);
  }, [newPassword]);

  const checkPasswordStrength = (password) => {
    if (!password) {
      setPasswordStrength({ score: 0, text: '', color: '' });
      return;
    }

    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;

    const strengths = [
      { score: 0, text: 'Very Weak', color: 'text-red-600' },
      { score: 1, text: 'Weak', color: 'text-orange-600' },
      { score: 2, text: 'Fair', color: 'text-yellow-600' },
      { score: 3, text: 'Good', color: 'text-blue-600' },
      { score: 4, text: 'Strong', color: 'text-green-600' },
      { score: 5, text: 'Very Strong', color: 'text-green-700' }
    ];

    setPasswordStrength(strengths[score]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!newPassword || !confirmPassword) {
      toast({
        title: 'Error',
        description: 'Please fill in all fields',
        variant: 'destructive'
      });
      return;
    }

    // Validate password strength
    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      toast({
        title: 'Password Requirements Not Met',
        description: passwordValidation.errors[0],
        variant: 'destructive'
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: 'Passwords Do Not Match',
        description: 'Please make sure both passwords match',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(
        `${API}/auth/change-password-first-login`,
        { new_password: newPassword },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast({
        title: 'Password Changed Successfully',
        description: 'Your password has been updated. Welcome to Hines Time!',
        duration: 5000
      });

      // Update user's first_login status in context
      const updatedUser = { ...user, first_login: false };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      // Route the user based on company policy + first-time setup state:
      //   - If their company requires 2FA → mandatory setup screen
      //   - Else if they've never seen the security upsell → voluntary upsell
      //   - Else straight to the dashboard
      setTimeout(() => {
        const company = (() => {
          try { return JSON.parse(localStorage.getItem('company') || 'null'); } catch { return null; }
        })();
        const seenKey = `security_setup_seen_${user?.id}`;
        const seen = localStorage.getItem(seenKey) === '1';
        if (company?.require_2fa && !updatedUser.two_factor_enabled) {
          navigate('/security-setup?required=2fa');
        } else if (!seen) {
          navigate('/security-setup');
        } else {
          navigate('/home');
        }
      }, 1500);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to change password. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    logout();
    navigate('/signin');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50 p-4">
      <Card className="w-full max-w-lg shadow-2xl border-2 border-blue-200">
        <CardHeader className="bg-gradient-to-r from-blue-600 to-green-600 text-white">
          <CardTitle className="flex items-center space-x-3 text-2xl">
            <div className="w-12 h-12 bg-[#1A2433] rounded-full flex items-center justify-center">
              <Lock className="w-6 h-6 text-blue-600" />
            </div>
            <span>Password Change Required</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-8">
          <div className="mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded-r-lg">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-yellow-900 mb-1">
                  First Login - Security Requirement
                </p>
                <p className="text-sm text-yellow-800">
                  You're using a temporary password. For security reasons, please create a new, secure password before continuing.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-base font-semibold">
                New Password
              </Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  name="newPassword"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter your new password"
                  className="h-12 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 focus:outline-none"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              <PasswordRequirements 
                password={newPassword} 
                showRequirements={newPassword.length > 0}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-base font-semibold">
                Confirm New Password
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your new password"
                  className="h-12 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 focus:outline-none"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {confirmPassword && (
                <div className="flex items-center space-x-2 mt-2">
                  {newPassword === confirmPassword ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-600 font-medium">Passwords match</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-4 h-4 text-red-600" />
                      <span className="text-sm text-red-600 font-medium">Passwords do not match</span>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-900 font-semibold mb-2">Password Requirements:</p>
              <ul className="text-sm text-blue-800 space-y-1">
                <li className="flex items-center">
                  <span className={newPassword.length >= 8 ? 'text-green-600' : ''}>
                    • At least 8 characters long
                  </span>
                </li>
                <li className="flex items-center">
                  <span className={/[A-Z]/.test(newPassword) ? 'text-green-600' : ''}>
                    • Contains uppercase letter
                  </span>
                </li>
                <li className="flex items-center">
                  <span className={/[a-z]/.test(newPassword) ? 'text-green-600' : ''}>
                    • Contains lowercase letter
                  </span>
                </li>
                <li className="flex items-center">
                  <span className={/\d/.test(newPassword) ? 'text-green-600' : ''}>
                    • Contains number
                  </span>
                </li>
              </ul>
            </div>

            <div className="flex space-x-4 pt-4">
              <Button
                type="submit"
                disabled={isLoading || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                className="flex-1 h-12 bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white font-bold"
              >
                {isLoading ? 'Changing Password...' : 'Change Password & Continue'}
              </Button>
              <Button
                type="button"
                onClick={handleCancel}
                variant="outline"
                className="h-12 border-2"
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChangePasswordFirstLogin;
